// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Edit, Trash2, Search, User } from "lucide-react";
import { useOfficials, useOfficialDetail } from "@/hooks/useOfficialsData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

const certColors: Record<string, string> = { certified: "bg-green-500 text-white", experienced: "bg-blue-500 text-white", beginner: "bg-gray-400 text-white" };
const statusColorMap: Record<string, string> = { active: "bg-green-500 text-white", inactive: "bg-gray-400 text-white", blacklisted: "bg-red-500 text-white" };

const emptyForm = { name: "", email: "", phone: "", city: "", state: "", certification_level: "experienced", sports: "basketball", default_pay_rate: "", pay_type: "per_game", shirt_size: "", emergency_contact_name: "", emergency_contact_phone: "", notes: "", status: "active" };

const OfficialRosterTab: React.FC = () => {
  const { data: officials, isLoading } = useOfficials();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [filterCert, setFilterCert] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  // Detail modal
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailOfficial = officials?.find((o: any) => o.id === detailId);
  const { data: detailAssignments } = useOfficialDetail(detailId);

  const filtered = useMemo(() => {
    return (officials || []).filter((o: any) => {
      if (search) {
        const s = search.toLowerCase();
        if (!o.name?.toLowerCase().includes(s) && !o.email?.toLowerCase().includes(s) && !o.city?.toLowerCase().includes(s)) return false;
      }
      if (filterCert !== "all" && o.certification_level !== filterCert) return false;
      if (filterStatus !== "all" && o.status !== filterStatus) return false;
      return true;
    });
  }, [officials, search, filterCert, filterStatus]);

  const handleOpen = (official?: any) => {
    if (official) {
      setEditId(official.id);
      setForm({
        name: official.name || "",
        email: official.email || "",
        phone: official.phone || "",
        city: official.city || "",
        state: official.state || "",
        certification_level: official.certification_level || "experienced",
        sports: Array.isArray(official.sports) ? official.sports.join(", ") : official.sports || "basketball",
        default_pay_rate: official.default_pay_rate?.toString() || "",
        pay_type: official.pay_type || "per_game",
        shirt_size: official.shirt_size || "",
        emergency_contact_name: official.emergency_contact_name || "",
        emergency_contact_phone: official.emergency_contact_phone || "",
        notes: official.notes || "",
        status: official.status || "active",
      });
    } else {
      setEditId(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const sportsArr = form.sports.split(",").map(s => s.trim()).filter(Boolean);
    const payload = {
      name: form.name,
      email: form.email || null,
      phone: form.phone || null,
      city: form.city || null,
      state: form.state || null,
      certification_level: form.certification_level,
      sports: sportsArr,
      default_pay_rate: form.default_pay_rate ? parseFloat(form.default_pay_rate) : null,
      pay_type: form.pay_type,
      shirt_size: form.shirt_size || null,
      emergency_contact_name: form.emergency_contact_name || null,
      emergency_contact_phone: form.emergency_contact_phone || null,
      notes: form.notes || null,
      status: form.status,
    };
    if (editId) {
      const { error } = await supabase.from("manage_officials").update(payload).eq("id", editId);
      if (error) { toast.error("Failed to update"); return; }
      toast.success("Official updated");
    } else {
      const { error } = await supabase.from("manage_officials").insert(payload);
      if (error) { toast.error("Failed to add"); return; }
      toast.success("Official added");
    }
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["officials"] });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this official?")) return;
    const { error } = await supabase.from("manage_officials").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Official deleted");
    queryClient.invalidateQueries({ queryKey: ["officials"] });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative min-w-[250px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search name, email, city..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div>
          <Label className="text-xs">Certification</Label>
          <Select value={filterCert} onValueChange={setFilterCert}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="certified">Certified</SelectItem>
              <SelectItem value="experienced">Experienced</SelectItem>
              <SelectItem value="beginner">Beginner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="blacklisted">Blacklisted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={() => handleOpen()} className="ml-auto"><Plus className="h-4 w-4 mr-1" />Add Official</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editId ? "Edit Official" : "Add Official"}</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Certification</Label>
                  <Select value={form.certification_level} onValueChange={(v) => setForm({ ...form, certification_level: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="certified">Certified</SelectItem>
                      <SelectItem value="experienced">Experienced</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Sports</Label><Input value={form.sports} onChange={(e) => setForm({ ...form, sports: e.target.value })} placeholder="basketball, football" /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Pay Rate ($)</Label><Input type="number" step="0.01" value={form.default_pay_rate} onChange={(e) => setForm({ ...form, default_pay_rate: e.target.value })} /></div>
                <div><Label>Pay Type</Label>
                  <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_game">Per Game</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="flat">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Shirt Size</Label>
                  <Select value={form.shirt_size} onValueChange={(v) => setForm({ ...form, shirt_size: v })}>
                    <SelectTrigger><SelectValue placeholder="Size" /></SelectTrigger>
                    <SelectContent>
                      {["S", "M", "L", "XL", "XXL", "XXXL"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Emergency Contact</Label><Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
                <div><Label>Emergency Phone</Label><Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="blacklisted">Blacklisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button onClick={handleSave} className="w-full">{editId ? "Update" : "Add"} Official</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-sm text-muted-foreground">{filtered.length} officials</p>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>City/State</TableHead>
                  <TableHead>Cert</TableHead>
                  <TableHead className="text-right">Pay Rate</TableHead>
                  <TableHead>Pay Type</TableHead>
                  <TableHead>Shirt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No officials found.</TableCell></TableRow>
                ) : filtered.map((o: any) => (
                  <TableRow key={o.id}>
                    <TableCell>
                      <Button variant="link" className="p-0 h-auto font-medium" onClick={() => setDetailId(o.id)}>{o.name}</Button>
                    </TableCell>
                    <TableCell className="text-sm">{o.email || "—"}</TableCell>
                    <TableCell className="text-sm">{o.phone || "—"}</TableCell>
                    <TableCell className="text-sm">{o.city ? `${o.city}, ${o.state || ""}` : "—"}</TableCell>
                    <TableCell><Badge className={certColors[o.certification_level] || "bg-gray-400 text-white"}>{o.certification_level}</Badge></TableCell>
                    <TableCell className="text-right">{o.default_pay_rate ? `$${Number(o.default_pay_rate).toFixed(0)}` : "—"}</TableCell>
                    <TableCell className="text-sm">{o.pay_type?.replace("_", " ") || "—"}</TableCell>
                    <TableCell className="text-sm">{o.shirt_size || "—"}</TableCell>
                    <TableCell><Badge className={statusColorMap[o.status] || "bg-gray-400 text-white"}>{o.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => handleOpen(o)}><Edit className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(o.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="h-5 w-5" />{detailOfficial?.name || "Official"}</DialogTitle></DialogHeader>
          {detailOfficial && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Email:</span> {detailOfficial.email || "—"}</div>
                <div><span className="text-muted-foreground">Phone:</span> {detailOfficial.phone || "—"}</div>
                <div><span className="text-muted-foreground">Location:</span> {detailOfficial.city ? `${detailOfficial.city}, ${detailOfficial.state}` : "—"}</div>
                <div><span className="text-muted-foreground">Certification:</span> <Badge className={certColors[detailOfficial.certification_level] || ""}>{detailOfficial.certification_level}</Badge></div>
                <div><span className="text-muted-foreground">Pay:</span> ${detailOfficial.default_pay_rate || 0}/{detailOfficial.pay_type?.replace("_", " ")}</div>
                <div><span className="text-muted-foreground">Shirt:</span> {detailOfficial.shirt_size || "—"}</div>
                <div><span className="text-muted-foreground">Emergency:</span> {detailOfficial.emergency_contact_name || "—"} {detailOfficial.emergency_contact_phone ? `(${detailOfficial.emergency_contact_phone})` : ""}</div>
                <div><span className="text-muted-foreground">Status:</span> <Badge className={statusColorMap[detailOfficial.status] || ""}>{detailOfficial.status}</Badge></div>
              </div>
              {detailOfficial.notes && <p className="text-sm text-muted-foreground border-t pt-2">{detailOfficial.notes}</p>}

              <div className="border-t pt-3">
                <h4 className="font-medium mb-2">Assignments</h4>
                {!detailAssignments || detailAssignments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No assignments found.</p>
                ) : (
                  <div className="space-y-1">
                    {detailAssignments.map((a: any) => (
                      <div key={a.id} className="flex items-center justify-between text-sm p-2 bg-muted/30 rounded">
                        <span>{a.event?.name || "Event"} — {a.assignment_date ? format(new Date(a.assignment_date), "MMM d, yyyy") : "No date"}</span>
                        <Badge className={`${assignmentStatusColors[a.status] || "bg-gray-400 text-white"} text-xs`}>{a.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const assignmentStatusColors: Record<string, string> = {
  invited: "bg-yellow-500 text-white",
  confirmed: "bg-blue-500 text-white",
  declined: "bg-red-500 text-white",
  checked_in: "bg-green-500 text-white",
  completed: "bg-purple-500 text-white",
  paid: "bg-green-600 text-white",
  no_show: "bg-red-600 text-white",
};

export default OfficialRosterTab;
